import { GTMEventTypes } from '@sial/common-utils'
import { ShareDialogItemTypes } from '@src/components/ShareDialog/ShareDialog'
import { sendEvent } from '@utils/analytics/coreAnalytics'
import { EventCategory } from '@utils/analytics/enums'

export const sendQuoteDetailsEvent = (
  action: string,
  quoteNumber: string,
  ga4Payload?: {
    clickAction: string
    elementType: string
    component: string
    linkText?: string
    linkUrl?: string
    quoteStatus: string
  },
  ga4Only: boolean = false
) => {
  const basePayload = {
    event: 'ga4Event',
    event_name: 'quotation_page_click',
    click_type: 'quotation: detail page',
    click_action: ga4Payload?.clickAction,
    click_details: quoteNumber || undefined,
    element_type: ga4Payload?.elementType,
    component: ga4Payload?.component,
    link_text: ga4Payload?.linkText?.toLowerCase() || undefined,
    link_url: ga4Payload?.linkUrl || undefined,
    quote_status: ga4Payload?.quoteStatus.toLowerCase(),
  }

  const legacyUAFields = {
    eventCategory: EventCategory.QuoteDetailsPage,
    eventAction: action,
    eventLabel: quoteNumber,
    eventInteractionType: 0,
  }

  const buildPayload = ga4Only
    ? { payload: basePayload }
    : {
        eventType: GTMEventTypes.AnalyticsEvent,
        payload: { ...basePayload, ...legacyUAFields },
      }

  sendEvent(buildPayload)
}

export const sendOpenItemShareModalEvent = (
  itemNumber: string | null | undefined
) => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      eventCategory: EventCategory.OrderCenter,
      eventAction: 'share link',
      eventLabel: itemNumber,
      eventInteractionType: 0,
    },
  })
}

export enum ShareItemDetailsModalEventAction {
  Close = 'close',
  Copy = 'copy',
  SendEmail = 'send email',
  Ignore = 'ignore',
}

export const sendShareItemDetailsModalEvent = (
  action: ShareItemDetailsModalEventAction,
  itemNumber: string | null | undefined,
  itemType: ShareDialogItemTypes
) => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      eventCategory: EventCategory.OrderCenter,
      eventAction: `share ${itemType} details modal - ${action}`,
      eventLabel: itemNumber,
      eventInteractionType: 0,
    },
  })
}

export enum EventMethods {
  Email = 'email',
  CopyLink = 'copy link',
  Print = 'print',
}

export const sendShareDialogEvent = ({
  action,
  itemNumber,
  contentType,
  method,
  component,
  elementType,
  elementText,
  productId,
  productBrand,
}: {
  action: ShareItemDetailsModalEventAction
  contentType: ShareDialogItemTypes
  method: EventMethods
  itemNumber: string | null | undefined
  component: string
  elementType: 'link' | 'button'
  elementText: string
  productId?: string | undefined
  productBrand?: string | undefined
}) => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      eventCategory: EventCategory.OrderCenter,
      eventAction: `share ${contentType} details modal - ${action}`,
      eventLabel: itemNumber,
      eventInteractionType: 0,
      event: 'share',
      content_type: `${contentType} details`,
      method,
      detail: itemNumber,
      component,
      element_type: elementType,
      element_text: elementText,
      product_id: productId,
      product_brand: productBrand,
    },
  })
}

export const sendOrderQuotesSelfAffiliationEvent = (
  action: string | null | undefined,
  eventLabel: string | null | undefined
) => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      eventCategory: EventCategory.OrderCenter,
      eventAction: `self-affiliation > connect for ${action}.`,
      eventLabel: eventLabel,
      eventInteractionType: 0,
    },
  })
}

// Account Dashboard Recent Orders Clicks

export const sendAccountDashboardRecentOrderClickEvent = (
  orderNumber: string | null | undefined
) => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      eventCategory: EventCategory.OrderCenter,
      eventAction: 'account recent orders - open order details',
      eventLabel: orderNumber,
      eventInteractionType: 0,
    },
  })
}

export const sendAccountDashboardViewAllOrdersClickEvent = (
  participantID: string | null | undefined
) => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      eventCategory: EventCategory.OrderCenter,
      eventAction: 'account recent orders - show all my orders',
      eventLabel: participantID,
      eventInteractionType: 0,
    },
  })
}

export const sendAccountDashboardOrderDownloadClickEvent = (
  orderNumber: string | null | undefined
) => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      eventCategory: EventCategory.Downloads,
      eventAction: 'account recent orders - order confirmation pdf',
      eventLabel: orderNumber,
      eventInteractionType: 0,
    },
  })
}

// Account Dashboard Recent Quotes Clicks

export const sendAccountDashboardViewAllQuotesClickEvent = (
  participantID: string | null | undefined
) => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      eventCategory: EventCategory.OrderCenter,
      eventAction: 'account recent quotes - view all quotes',
      eventLabel: participantID,
      eventInteractionType: 0,
    },
  })
}

export const sendAccountDashboardCreateQuoteClickEvent = (
  participantID: string | null | undefined
) => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      eventCategory: EventCategory.OrderCenter,
      eventAction: 'account recent quotes - create quote',
      eventLabel: participantID,
      eventInteractionType: 0,
    },
  })
}

export const sendOrderCenterEvent = (
  eventAction: string,
  eventLabel: string
) => {
  sendEvent({
    eventType: GTMEventTypes.AnalyticsEvent,
    payload: {
      eventCategory: EventCategory.OrderCenter,
      eventAction,
      eventLabel,
      eventInteractionType: 0,
    },
  })
}
